import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  UserPlus,
  Mail,
  Shield,
  AlertCircle,
  RefreshCcw,
  Edit3,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useGetRolesSelectQuery,
  useInviteUserMutation,
  useUpdateUserMutation,
  User,
} from '@/redux/service/userManagement'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'

interface InviteUserModalProps {
  isOpen: boolean
  onClose: () => void
  user?: User | null
  prefill?: { name?: string; email?: string }
  onCreated?: (info: { name: string; email: string }) => void
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  user,
  prefill,
  onCreated,
}) => {
  const { data: rolesData } = useGetRolesSelectQuery()
  const isEditMode = !!user

  const [inviteUser, { isLoading: isInviting }] = useInviteUserMutation()
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation()

  const formik = useFormik({
    initialValues: {
      name: user?.name || prefill?.name || '',
      email: user?.email || prefill?.email || '',
      role_id: user?.role_id || (null as number | null),
      status: user?.status || 'active',
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string()
        .min(2, 'Name is too short')
        .required('Full name is required'),
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
      role_id: Yup.number().required('Please select a role').nullable(),
      status: Yup.string().required('Status is required'),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        if (isEditMode) {
          await updateUser({
            user_id: user!.id,
            role_id: values.role_id!,
            status: values.status,
          }).unwrap()
          toast.success('User updated successfully')
        } else {
          const res = await inviteUser({
            name: values.name,
            email: values.email,
            role_id: values.role_id!,
          }).unwrap()
          toast.success(res?.message || `Invitation sent to ${values.email}`)
          onCreated?.({ name: values.name, email: values.email })
        }
        resetForm()
        onClose()
      } catch (err) {
        const error = err as { data?: { message?: string } }
        toast.error(
          error?.data?.message ??
            `Failed to ${isEditMode ? 'update user' : 'send invitation'}`
        )
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className='max-w-md p-0 overflow-hidden bg-background border-border rounded-xl shadow-2xl'>
        {/* HEADER */}
        <div className='px-8 py-6 border-b border-border flex items-center justify-between bg-muted/30'>
          <div className='flex items-center gap-4'>
            <div className='h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-sm'>
              {isEditMode ? (
                <Edit3 className='w-6 h-6' />
              ) : (
                <UserPlus className='w-6 h-6' />
              )}
            </div>
            <div className='space-y-1'>
              <DialogTitle className='text-xl font-bold text-foreground tracking-tight'>
                {isEditMode ? 'Update User' : 'Team Invitation'}
              </DialogTitle>
              <DialogDescription className='text-sm text-muted-foreground'>
                {isEditMode
                  ? 'Modify account details and permissions'
                  : 'Invite a new member to your organization'}
              </DialogDescription>
            </div>
          </div>
          {isEditMode && user?.status === 'invited' && (
            <Button
              type='button'
              variant='ghost'
              size='icon'
              disabled={isInviting}
              onClick={async () => {
                try {
                  const res = await inviteUser({
                    name: formik.values.name,
                    email: formik.values.email,
                    role_id: formik.values.role_id!,
                  }).unwrap()
                  toast.success(
                    res?.message ||
                      `Invitation resent to ${formik.values.email}`
                  )
                } catch (err) {
                  const error = err as { data?: { message?: string } }
                  toast.error(
                    error?.data?.message ?? 'Failed to resend invitation'
                  )
                }
              }}
              className='h-9 w-9 rounded-lg hover:bg-primary/10 hover:text-primary transition-all'
              title='Resend Invitation'
            >
              <RefreshCcw
                className={cn('h-4 w-4', isInviting && 'animate-spin')}
              />
            </Button>
          )}
        </div>

        <form onSubmit={formik.handleSubmit} className='flex flex-col'>
          <div className='p-8 space-y-6'>
            {/* NAME & EMAIL */}
            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label
                  htmlFor='name'
                  className='text-xs font-bold uppercase tracking-wider text-muted-foreground px-1'
                >
                  Full Name <span className='text-red-500'>*</span>
                </Label>
                <div className='relative group'>
                  <UserPlus className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors' />
                  <Input
                    id='name'
                    name='name'
                    placeholder='e.g. John Doe'
                    disabled={isEditMode}
                    className={cn(
                      'pl-10 h-10 border-border rounded-lg focus:ring-1 focus:ring-primary transition-all',
                      formik.touched.name &&
                        formik.errors.name &&
                        'border-destructive',
                      isEditMode && 'bg-muted/50 cursor-not-allowed'
                    )}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                {formik.touched.name && formik.errors.name && (
                  <p className='text-[11px] font-medium text-destructive px-1 flex items-center gap-1'>
                    <AlertCircle className='h-3 w-3' />
                    {formik.errors.name}
                  </p>
                )}
              </div>

              <div className='space-y-2'>
                <Label
                  htmlFor='email'
                  className='text-xs font-bold uppercase tracking-wider text-muted-foreground px-1'
                >
                  Email Address <span className='text-red-500'>*</span>
                </Label>
                <div className='relative group'>
                  <Mail className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors' />
                  <Input
                    id='email'
                    name='email'
                    type='email'
                    placeholder='john@example.com'
                    disabled={isEditMode}
                    className={cn(
                      'pl-10 h-10 border-border rounded-lg focus:ring-1 focus:ring-primary transition-all',
                      formik.touched.email &&
                        formik.errors.email &&
                        'border-destructive',
                      isEditMode && 'bg-muted/50 cursor-not-allowed'
                    )}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                {formik.touched.email && formik.errors.email && (
                  <p className='text-[11px] font-medium text-destructive px-1 flex items-center gap-1'>
                    <AlertCircle className='h-3 w-3' />
                    {formik.errors.email}
                  </p>
                )}
              </div>
            </div>

            {/* ROLE & STATUS */}
            <div className='flex  gap-4'>
              <div className='space-y-2 w-full'>
                <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground px-1'>
                  Assign Role <span className='text-red-500'>*</span>
                </Label>
                <Select
                  value={formik.values.role_id?.toString() || ''}
                  onValueChange={value =>
                    formik.setFieldValue(
                      'role_id',
                      value ? Number(value) : null
                    )
                  }
                >
                  <SelectTrigger
                    className={cn(
                      'h-10 border-border rounded-lg bg-background hover:bg-muted/30 transition-all capitalize',
                      formik.touched.role_id &&
                        formik.errors.role_id &&
                        'border-destructive'
                    )}
                  >
                    <div className='flex items-center gap-2'>
                      <Shield className='h-3.5 w-3.5 text-muted-foreground' />
                      <SelectValue placeholder='Select role...' />
                    </div>
                  </SelectTrigger>
                  <SelectContent className='rounded-xl shadow-xl capitalize'>
                    {rolesData?.data?.map(role => (
                      <SelectItem key={role.id} value={role.id.toString()}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isEditMode && (
                <div className='space-y-2 w-full'>
                  <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground px-1'>
                    Account Status
                  </Label>
                  <Select
                    value={formik.values.status}
                    onValueChange={value =>
                      formik.setFieldValue('status', value)
                    }
                  >
                    <SelectTrigger className='h-10 border-border rounded-lg bg-background hover:bg-muted/30 transition-all capitalize'>
                      <SelectValue placeholder='Status' />
                    </SelectTrigger>
                    <SelectContent className='rounded-xl shadow-xl'>
                      <SelectItem value='active'>Active</SelectItem>
                      <SelectItem value='inactive'>Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className='p-3 rounded-lg bg-muted/20 border border-border/50'>
              <p className='text-[10px] text-muted-foreground leading-relaxed'>
                <span className='font-bold uppercase tracking-tight text-foreground/70 mr-1'>
                  Note:
                </span>
                Team membership and additional employee settings are managed
                through the Employees dashboard.
              </p>
            </div>
          </div>

          {/* FOOTER */}
          <div className='px-8 py-4 border-t border-border flex gap-3 bg-muted/10'>
            <Button
              type='button'
              variant='outline'
              onClick={onClose}
              className='flex-1 h-10 rounded-lg border-border hover:bg-muted transition-all'
            >
              Cancel
            </Button>
            <Button
              type='submit'
              disabled={isInviting || isUpdating || !formik.isValid}
              className='flex-1 h-10 rounded-lg bg-primary text-primary-foreground shadow-sm shadow-primary/10'
            >
              {isInviting || isUpdating ? (
                <div className='flex items-center gap-2'>
                  <div className='w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin' />
                  <span>{isEditMode ? 'Updating...' : 'Sending...'}</span>
                </div>
              ) : isEditMode ? (
                'Update User'
              ) : (
                'Send Invite'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default InviteUserModal
